import React, { useState } from "react";
import LogoIcon from "../../assets/images/login_form_logo.png";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import bgImage from "../../assets/images/bg-img.jpg";
import { Eye, EyeOff } from "lucide-react";
import { MdEmail } from "react-icons/md";
import { FaLongArrowAltRight } from "react-icons/fa";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [userType, setUserType] = useState("");

  const navigate = useNavigate();

  const validateForm = () => {
    let tempErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!userType) {
      tempErrors.userType = "User type is required";
    }

    if (!email) {
      tempErrors.email = "Email is required";
    } else if (!emailPattern.test(email)) {
      tempErrors.email = "Invalid email format";
    }

    if (!password) {
      tempErrors.password = "Password is required";
    } else if (password.length < 6) {
      tempErrors.password = "Password must be at least 6 characters";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log("Email:", email);
      console.log("Password:", password);
      setLoading(true);
      try {
        const payload = {
          email,
          password,
          user_type: userType,
          remember_me: rememberMe,
        };

        const login = await axios.post(
          `${process.env.REACT_APP_API_KEY}/login`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        console.log(login, "***login****");
        toast.success("Login Successfully!", {
          style: { backgroundColor: "green", color: "white" },
        });

        localStorage.setItem("access_token", login?.data?.token);
        localStorage.setItem("user_id", JSON.stringify(login?.data?.user_id));
        localStorage.setItem("user_type", JSON.stringify(userType));

        // Redirect based on userType
        if (userType === "Candidate") {
          navigate(`/candidate-jobs?candidate_id=${login?.data?.user_id}`);
        } else if (userType === "Team Lead") {
          navigate(`/created-jobs?user_id=${login?.data?.user_id}`);
        } else if (userType === "HR") {
          navigate("/compare-resume");
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data?.error, {
          style: { backgroundColor: "red", color: "white" },
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div
      className="  flex justify-center items-center min-h-[calc(100vh-20px)] px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full max-w-6xl shadow-xl rounded-2xl bg-white overflow-hidden">
        {/* Left Side - Image Section */}
        <div className="flex justify-center items-center p-6 bg-gray-100">
          <img
            src={LogoIcon}
            className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xs"
            alt="Login Illustration"
          />
        </div>

        {/* Right Side - Login Form */}
        <div className="p-6 sm:p-8 w-full">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-700">
              Welcome Back
            </h2>
            <p className="text-gray-500 text-sm sm:text-base mt-1">
              Sign in to continue
            </p>
          </div>

          <form onSubmit={handleLogin}>
            {/* Email Input */}
            <div className="relative mb-4">
              <label className="block text-sm sm:text-base font-medium text-gray-700">
                Select User Type :
              </label>
              <select
                className={`w-full px-4 py-2 mt-1 border rounded-lg text-sm sm:text-base ${
                  errors.userType ? "border-red-500" : ""
                }`}
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="Candidate">Candidate</option>
                <option value="HR">HR</option>
                <option value="Team Lead">Team Lead</option>
                <option value="Rishbh Shah" className="hidden">
                  Owner
                </option>
              </select>
              {errors.userType && (
                <p className="text-red-500 text-xs mt-1">{errors.userType}</p>
              )}
            </div>
            <div className="relative mb-4">
              <label className="block text-sm sm:text-base font-medium text-gray-700">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 mt-1 border rounded-lg focus:ring focus:ring-blue-300 text-sm sm:text-base pr-10 ${
                    errors.email ? "border-red-500" : ""
                  }`}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <MdEmail
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  size={20}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div className="relative mb-4">
              <label className="block text-sm sm:text-base font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 mt-1 border rounded-lg focus:ring focus:ring-blue-300 text-sm sm:text-base ${
                    errors.password ? "border-red-500" : ""
                  }`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password  */}
            <div className="flex justify-between items-center text-sm sm:text-base mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <a href="/" className="text-blue-500 hover:underline">
                Forgot password?
              </a>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              className="w-full bg-blue-700 hover:bg-blue-800 text-white py-2 sm:py-3 rounded-lg shadow-lg transition text-sm sm:text-base font-semibold  disabled:bg-gray-500 disabled:cursor-not-allowed"
              disabled={loading}
            >
              LOG IN
            </button>

            {/* Register Link */}
            <p className="text-center text-sm sm:text-base text-gray-600 mt-4 ">
              Don’t have any account?{" "}
              <a
                href="/sign-up"
                className="text-blue-700 font-semibold hover:underline text-blue-800"
              >
                Sign Up
                <FaLongArrowAltRight className="inline text-blue-700  ml-1" />
              </a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginForm;
